<script setup lang="ts">
import { computed, toRef } from 'vue';
import { DOC_TYPE_LABELS } from '@/constants';
import {
  DocType,
  FileNameSearchResponse,
  IdentifyValue,
  SearchScope,
  TagType,
} from '@/types';
import { useGroups, useGroupTagsNonSticky } from '@/utils/swr';
import SurveyFilterTag from './survey-filter-tag.vue';

const props = defineProps<{
  enableDelete: boolean;
  docTypes: Array<DocType | 'user_document'>;
  searchScope: SearchScope;
  specifiedFiles: Array<FileNameSearchResponse>;
  groupId?: number;
  groupTagIds?: number[];
}>();

defineEmits<{
  deleteTag: [payload: { filterType: TagType; identifyValue: IdentifyValue }];
}>();

const docTypeLabel = computed(() => {
  return props.docTypes.length > 0 ? '情報種別' : 'ファイル';
});

const combinedTags = computed(() => {
  // 「検索対象絞り込み」と「ファイル指定」は排他的に選択する仕様のため、どちらか片方しか存在しない
  if (props.docTypes.length > 0) {
    const tagLabelPrefix =
      props.searchScope === 'user_marks'
        ? 'マークした'
        : props.searchScope === 'group_marks'
          ? 'グループマークした'
          : '';
    return props.docTypes.map(docType => ({
      key: docType,
      type: 'docTypes' as TagType,
      tagLabel: tagLabelPrefix + DOC_TYPE_LABELS[docType],
      enableDelete: props.enableDelete,
      identifyValue: docType,
      file: undefined,
    }));
  } else if (props.specifiedFiles.length > 0) {
    return props.specifiedFiles.map((file: FileNameSearchResponse) => ({
      key: file.id,
      type: 'specifiedFiles' as TagType,
      tagLabel: file.filename,
      enableDelete: props.enableDelete,
      identifyValue: file,
      file,
    }));
  }

  return [];
});

const { data: groups } = useGroups();
const selectedGroupName = computed(() => {
  if (!groups.value || !props.groupId) return '';
  const group = groups.value.groups.find(g => g.id === props.groupId);
  return group?.name;
});

const { data: groupTags } = useGroupTagsNonSticky(toRef(props, 'groupId'));
const groupTagIdToName = computed(() => {
  if (!groupTags.value) return {};
  return Object.fromEntries(
    groupTags.value.tags.map(tag => [tag.id, tag.name]),
  );
});
</script>

<template>
  <div class="survey-selected-filters selected-values-container">
    <div
      v-if="combinedTags.length > 0"
      class="selected-values-label c-text-regular--s"
    >
      {{ docTypeLabel }}
    </div>
    <div class="selected-values">
      <TransitionGroup name="fade-slide">
        <SurveyFilterTag
          v-for="tag in combinedTags"
          :key="tag.key"
          :type="tag.type"
          :tag-label="tag.tagLabel"
          :identify-value="tag.identifyValue"
          :file="tag.file"
          :enable-delete="enableDelete"
          @delete-tag="$emit('deleteTag', $event)"
        />
      </TransitionGroup>
    </div>
    <template v-if="groupId && selectedGroupName">
      <div class="selected-values-label c-text-regular--s">
        参加済みのグループ
      </div>
      <div class="selected-values">
        <TransitionGroup name="fade-slide">
          <SurveyFilterTag
            type="groupId"
            :tag-label="selectedGroupName"
            :identify-value="groupId"
            :enable-delete="enableDelete"
            @delete-tag="$emit('deleteTag', $event)"
          />
        </TransitionGroup>
      </div>
    </template>
    <template v-if="(groupTagIds?.length ?? 0) > 0 && groupTags">
      <div class="selected-values-label c-text-regular--s">グループタグ</div>
      <div class="selected-values">
        <TransitionGroup name="fade-slide">
          <SurveyFilterTag
            v-for="tagId in groupTagIds"
            :key="tagId"
            type="groupTags"
            :tag-label="groupTagIdToName[tagId]"
            :identify-value="tagId"
            :enable-delete="enableDelete"
            @delete-tag="$emit('deleteTag', $event)"
          />
        </TransitionGroup>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.survey-selected-filters {
  margin-top: 8px;
}

.selected-values-container {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px;
  align-items: center;
}

.selected-values-label {
  color: $color-gray800;
}

.selected-values {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

// Vue.jsのTransitionを使ったアニメーション
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.2s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
.fade-slide-enter-to,
.fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
